import { BaseApiService } from '../base/BaseApiService';
import { ApiResponse } from '../../types/api';

export enum QuizAttemptStatus {
  InProgress = 1,
  Completed = 2,
  Abandoned = 3
}

export interface AvailableQuizDto {
  id: string;
  title: string;
  description?: string;
  category?: string;
  minimumScore: number;
  timeLimitMinutes?: number;
  questionsCount: number;
  isAvailable: boolean;
  unavailableReason?: string;
  remainingAttempts?: number;
  releaseDate?: string;
  hasPrerequisites: boolean;
  prerequisiteQuizIds: string[];
}

export interface QuizAttemptDto {
  id: string;
  quizId: string;
  quizTitle: string;
  userId: string;
  userName: string;
  trainingClassId?: string;
  trainingClassName?: string;
  startedAt: string;
  completedAt?: string;
  timeSpentSeconds?: number;
  score?: number;
  isPassed?: boolean;
  status: QuizAttemptStatus;
  answers: AnswerDto[];
}

export interface StartQuizAttemptDto {
  quizId: string;
  trainingClassId?: string;
}

export interface SubmitAnswerDto {
  questionId: string;
  questionOptionId?: string;
}

export interface AnswerDto {
  id: string;
  questionId: string;
  questionText: string;
  questionOptionId?: string;
  selectedOptionText?: string;
  isCorrect: boolean;
  pointsEarned: number;
}

export interface QuizAttemptHistoryDto {
  attemptId: string;
  quizId: string;
  quizTitle: string;
  startedAt: string;
  completedAt?: string;
  timeSpentSeconds?: number;
  score?: number;
  isPassed?: boolean;
  status: QuizAttemptStatus;
  attemptNumber: number;
}

export class QuizAttemptService extends BaseApiService {
  async getAvailableQuizzes(trainingClassId?: string): Promise<AvailableQuizDto[]> {
    const params = trainingClassId ? { trainingClassId } : {};
    const response = await this.api.get<ApiResponse<AvailableQuizDto[]>>('/quiz-attempts/available', { params });
    return this.extractData(response);
  }

  async startQuizAttempt(data: StartQuizAttemptDto): Promise<QuizAttemptDto> {
    const response = await this.api.post<ApiResponse<QuizAttemptDto>>('/quiz-attempts/start', data);
    return this.extractData(response);
  }

  async submitAnswer(attemptId: string, data: SubmitAnswerDto): Promise<void> {
    await this.api.post(`/quiz-attempts/${attemptId}/answers`, data);
  }

  async completeQuizAttempt(attemptId: string): Promise<QuizAttemptDto> {
    const response = await this.api.post<ApiResponse<QuizAttemptDto>>(`/quiz-attempts/${attemptId}/complete`);
    return this.extractData(response);
  }

  async getQuizAttempt(id: string): Promise<QuizAttemptDto> {
    const response = await this.api.get<ApiResponse<QuizAttemptDto>>(`/quiz-attempts/${id}`);
    return this.extractData(response);
  }

  async getUserQuizAttemptHistory(userId: string): Promise<QuizAttemptHistoryDto[]> {
    const response = await this.api.get<ApiResponse<QuizAttemptHistoryDto[]>>(`/quiz-attempts/user/${userId}`);
    return this.extractData(response);
  }

  async getActiveAttempt(): Promise<QuizAttemptDto | null> {
    try {
      const response = await this.api.get<ApiResponse<QuizAttemptDto>>('/quiz-attempts/active');
      return this.extractData(response);
    } catch (error: any) {
      // Se retornar 404, não há tentativa ativa
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async abandonQuizAttempt(attemptId: string): Promise<void> {
    try {
      await this.api.post(`/quiz-attempts/${attemptId}/abandon`);
    } catch (error: any) {
      // Se endpoint não existir ainda, apenas logar erro mas não falhar
      console.warn('Erro ao abandonar tentativa (endpoint pode não existir):', error);
    }
  }

  async grantExtraAttempts(quizId: string, userId: string, extraAttempts: number = 1): Promise<void> {
    await this.api.post(`/quiz-attempts/quizzes/${quizId}/users/${userId}/grant-extra-attempts`, {
      extraAttempts,
    });
  }
}

export const quizAttemptService = new QuizAttemptService();
export default quizAttemptService;

